import { useCallback, useMemo, useRef } from 'react';

import { getUserLabel } from '@iconik-io/core/src/get_user_label';
import { truthyPredicate } from '@iconik-io/core/src/truthy_predicate';
import {
  type FilesHttpGetStoragesQueryPatched,
  type FilesStorageSchemaPatched,
  getStoragesRequest,
} from '@iconik-io/iconik-api/src/schema_patched/files_storages';
import type { MetadataFieldOptionsSchema } from '@iconik-io/iconik-api/src/services/metadata';
import {
  httpGetFaceRecognitionPersons,
  type MlHttpGetFaceRecognitionPersonsQuery,
} from '@iconik-io/iconik-api/src/services/ml';
import { httpGetUsersBasic } from '@iconik-io/iconik-api/src/services/users';
import { isMetadataFieldType } from '@iconik-io/iconik-api/src/types/metadata_field_type';
import {
  OBJECT_TYPE_PERSONS,
  OBJECT_TYPE_USERS,
} from '@iconik-io/iconik-api/src/types/object_types';
import { useLatest } from '@iconik-io/shared-lib/src/react/hooks/use_latest';
import type { MultiSelectFilterValue } from '@iconik-io/shared-lib/src/redux/common_filters/types';
import { cancellable } from '@iconik-io/shared-lib/src/redux/middlewares/http/actions';

import {
  MULTI_SELECT_TEXT_FIELD_TYPES,
  type MultiSelectTextFieldType,
} from '../../../../constants/search_mappings';
import {
  getStorageLogoUrl,
  STORAGE_PURPOSE_TERM_NAMES,
} from '../../../../constants/storages';
import { getSearchSuggestions } from '../../../../ducks/search_suggest/thunks';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../store/react_redux_typed';
import { selectUserRoles } from '../../../../utils/get_user_roles';
import { getItemLabelsForObjectType } from '../../../../utils/object_type';
import { prepareQueryParameter } from '../../../../utils/search_utils/search_utils';
import type {
  MultiSelectFilterFieldLabels,
  MultiSelectFilterItemDisplay,
} from './types';

/**
 * The reusable per-type pieces of a multi-select filter: the async option
 * loader, the selected-item resolver, and display metadata. Shared by the
 * panel field wrappers and the search filter chips so both edit the same
 * filter the same way.
 */
export type MultiSelectFilterOptions = {
  loadOptions: (query: string) => Promise<MultiSelectFilterItemDisplay[]>;
  resolveSelectedItems: (
    ids: string[],
  ) => Promise<MultiSelectFilterItemDisplay[]>;
  itemLabels: MultiSelectFilterFieldLabels;
  showItemAvatar?: boolean;
  showStartTypingHint?: boolean;
  /** Empty-query hint shown when `showStartTypingHint` is set. */
  startTypingHint?: string;
  /**
   * Optional transform applied to a value before it is stored, for filters
   * that annotate their groups (e.g. storage adds per-item ES term names).
   */
  enrichValue?: (
    value: MultiSelectFilterValue | undefined,
  ) => MultiSelectFilterValue | undefined;
};

export function useUserFilterOptions(
  searchKey = 'default',
): MultiSelectFilterOptions {
  const dispatch = useAppDispatch();
  const userRoles = useAppSelector(selectUserRoles);
  const canListUsers = userRoles.webCanListUsers;

  const loadOptions = useCallback(
    async (query: string): Promise<MultiSelectFilterItemDisplay[]> => {
      if (!canListUsers) {
        return [];
      }
      const response = await dispatch(
        cancellable(
          httpGetUsersBasic({
            query: prepareQueryParameter(query),
            sort: 'first_name,asc',
            per_page: 100,
          }),
          `multi-select-user-search-${searchKey}`,
        ),
      );
      return response.body.objects.map((user) => ({
        id: user.id,
        label: getUserLabel(user),
        imageUrl: user.photo_small ?? undefined,
      }));
    },
    [canListUsers, searchKey],
  );

  const resolveSelectedItems = useCallback(
    async (ids: string[]): Promise<MultiSelectFilterItemDisplay[]> => {
      if (ids.length === 0 || !canListUsers) {
        return [];
      }
      const response = await dispatch(
        httpGetUsersBasic({ ids: ids.join(','), per_page: 100 }),
      );
      const itemsById = new Map<string, MultiSelectFilterItemDisplay>();
      for (const user of response.body.objects) {
        itemsById.set(user.id, {
          id: user.id,
          label: getUserLabel(user),
          imageUrl: user.photo_small ?? undefined,
        });
      }
      return ids.map((id) => itemsById.get(id)).filter(truthyPredicate);
    },
    [canListUsers],
  );

  return {
    loadOptions,
    resolveSelectedItems,
    itemLabels: getItemLabelsForObjectType(OBJECT_TYPE_USERS),
  };
}

export function usePersonFilterOptions(): MultiSelectFilterOptions {
  const dispatch = useAppDispatch();
  const persons = useAppSelector((state) => state.entities.persons);
  const personsRef = useLatest(persons);

  const loadOptions = useCallback(
    async (query: string): Promise<MultiSelectFilterItemDisplay[]> => {
      const queryValue = prepareQueryParameter(query, { escapeMinus: true });
      const params: MlHttpGetFaceRecognitionPersonsQuery = {
        per_page: 20,
        status_in: 'USER_CONFIRMED,SYSTEM_CONFIRMED',
        sort: 'name,asc',
      };
      if (queryValue) {
        params.name = queryValue;
      }
      const response = await dispatch(
        cancellable(
          httpGetFaceRecognitionPersons(params),
          'multi-select-person-search',
        ),
      );
      return response.body.objects.map((item) => ({
        id: item.id,
        label: item.name ?? '',
        imageUrl: item.image_url ?? undefined,
      }));
    },
    [],
  );

  const resolveSelectedItems = useCallback(
    async (ids: string[]): Promise<MultiSelectFilterItemDisplay[]> => {
      const currentPersons = personsRef.current;
      const itemsById = new Map<string, MultiSelectFilterItemDisplay>();
      for (const id of ids) {
        const cached = currentPersons[id];
        if (cached) {
          itemsById.set(id, {
            id,
            label: cached.name ?? '',
            imageUrl: cached.image_url ?? undefined,
          });
        }
      }
      const unresolvedIds = ids.filter((id) => !itemsById.has(id));
      if (unresolvedIds.length > 0) {
        const params: MlHttpGetFaceRecognitionPersonsQuery = {
          ids: unresolvedIds.join(','),
          per_page: unresolvedIds.length,
        };
        const response = await dispatch(httpGetFaceRecognitionPersons(params));
        for (const item of response.body.objects) {
          itemsById.set(item.id, {
            id: item.id,
            label: item.name ?? '',
            imageUrl: item.image_url ?? undefined,
          });
        }
      }
      return ids.map((id) => itemsById.get(id)).filter(truthyPredicate);
    },
    [],
  );

  return {
    loadOptions,
    resolveSelectedItems,
    itemLabels: getItemLabelsForObjectType(OBJECT_TYPE_PERSONS),
  };
}

const resolveStorageTermName = (purpose?: string | null) =>
  STORAGE_PURPOSE_TERM_NAMES[purpose ?? ''] ?? 'files.storage_id';

const getStorageEndpoint = (settings: FilesStorageSchemaPatched['settings']) =>
  'endpoint' in settings && typeof settings.endpoint === 'string'
    ? settings.endpoint
    : undefined;

export type StorageFilterOptionsConfig = {
  params?: FilesHttpGetStoragesQueryPatched;
  /** Adds a flat `storage_id` term so collections mapped to a storage also match. */
  includeCollectionsTerm?: boolean;
  /** Stores groups without per-item term names (e.g. collection filters). */
  omitItemTermNames?: boolean;
  /** Current value, used to seed the storageId → term-name map on first render. */
  value?: MultiSelectFilterValue;
};

export function useStorageFilterOptions(
  config: StorageFilterOptionsConfig = {},
): MultiSelectFilterOptions {
  const { includeCollectionsTerm, omitItemTermNames } = config;
  const dispatch = useAppDispatch();
  const storages = useAppSelector((state) => state.entities.storages);
  const storagesRef = useLatest(storages);
  const paramsRef = useLatest(config.params);

  // Maps storageId → ES term name (e.g. 'proxies.storage_id'), populated as
  // storages are fetched so enrichValue can annotate groups.
  const termNameMapRef = useRef<Map<string, string>>(new Map());
  const seededRef = useRef(false);
  if (!seededRef.current) {
    seededRef.current = true;
    for (const group of config.value ?? []) {
      if (group.itemTermNames) {
        for (const [id, termName] of Object.entries(group.itemTermNames)) {
          termNameMapRef.current.set(id, termName);
        }
      }
    }
  }

  const toDisplay = useCallback(
    (storage: {
      id: string;
      name: string;
      purpose?: string | null;
      method?: string | null;
      settings: FilesStorageSchemaPatched['settings'];
    }): MultiSelectFilterItemDisplay => {
      termNameMapRef.current.set(
        storage.id,
        resolveStorageTermName(storage.purpose),
      );
      return {
        id: storage.id,
        label: storage.name,
        imageUrl: getStorageLogoUrl(
          storage.method,
          getStorageEndpoint(storage.settings),
        ),
        imageType: 'logo',
      };
    },
    [],
  );

  const loadOptions = useCallback(
    async (query: string): Promise<MultiSelectFilterItemDisplay[]> => {
      const response = await dispatch(
        cancellable(
          getStoragesRequest({
            query: prepareQueryParameter(query),
            per_page: 100,
            sort: 'name,asc',
            ...paramsRef.current,
          }),
          'multi-select-storage-search',
        ),
      );
      return response.body.objects.map(toDisplay);
    },
    [toDisplay],
  );

  const resolveSelectedItems = useCallback(
    async (ids: string[]): Promise<MultiSelectFilterItemDisplay[]> => {
      const currentStorages = storagesRef.current;
      const results: MultiSelectFilterItemDisplay[] = [];
      for (const id of ids) {
        const cached = currentStorages[id];
        if (cached) {
          results.push(toDisplay(cached));
        }
      }
      const unresolvedIds = ids.filter(
        (id) => !results.some((result) => result.id === id),
      );
      if (unresolvedIds.length === 0) {
        return results;
      }
      const response = await dispatch(
        getStoragesRequest({ ids: unresolvedIds.join(','), per_page: 100 }),
      );
      for (const storage of response.body.objects) {
        results.push(toDisplay(storage));
      }
      return results;
    },
    [toDisplay],
  );

  const enrichValue = useCallback(
    (value: MultiSelectFilterValue | undefined) =>
      value?.map((group) => {
        if (omitItemTermNames) {
          return {
            ...group,
            itemTermNames: undefined,
            additionalTermNames: undefined,
          };
        }
        const itemTermNames = Object.fromEntries(
          group.items
            .map((id) => [id, termNameMapRef.current.get(id)])
            .filter(
              (entry): entry is [string, string] => entry[1] !== undefined,
            ),
        );
        return {
          ...group,
          itemTermNames:
            Object.keys(itemTermNames).length > 0 ? itemTermNames : undefined,
          additionalTermNames: includeCollectionsTerm
            ? ['storage_id']
            : undefined,
        };
      }),
    [omitItemTermNames, includeCollectionsTerm],
  );

  return {
    loadOptions,
    resolveSelectedItems,
    itemLabels: { singular: 'storage', plural: 'storages' },
    enrichValue,
  };
}

export type MultiSelectFilterTextInputType = MultiSelectTextFieldType;

const isTextInputType = (
  fieldType: string | null | undefined,
): fieldType is MultiSelectFilterTextInputType =>
  typeof fieldType === 'string' &&
  isMetadataFieldType(fieldType) &&
  MULTI_SELECT_TEXT_FIELD_TYPES.has(fieldType);

const INTEGER_PATTERN = /^[+-]?\d+$/;
const FLOAT_PATTERN = /^[+-]?(\d+(\.\d+)?|\.\d+)$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const URL_PATTERN = /^(https?:\/\/)?([\w-]+\.)+[a-z]{2,}(:\d+)?(\/\S*)?$/i;

export function isValidTextTerm(
  term: string,
  inputType: MultiSelectFilterTextInputType,
): boolean {
  switch (inputType) {
    case 'integer':
      return INTEGER_PATTERN.test(term);
    case 'float':
      return FLOAT_PATTERN.test(term);
    case 'email':
      return EMAIL_PATTERN.test(term);
    case 'url':
      return URL_PATTERN.test(term);
    default:
      return true;
  }
}

// Free-text term options: match the currently-selected terms and offer the
// trimmed query as a creatable term when it's valid for the input type. Shared
// by the transcription-text field hook and the dynamic metadata-text factory.
function buildTextTermOptions(
  value: MultiSelectFilterValue | undefined,
  inputType: MultiSelectFilterTextInputType,
  showStartTypingHint: boolean,
): MultiSelectFilterOptions {
  return {
    loadOptions: async (query) => {
      const trimmed = query.trim();
      if (!trimmed) {
        return [];
      }
      const lowerQuery = trimmed.toLowerCase();
      const existingTerms = [
        ...new Set(value?.flatMap((group) => group.items) ?? []),
      ];
      const matchingExisting = existingTerms
        .filter((term) => term.toLowerCase().includes(lowerQuery))
        .map((term) => ({ id: term, label: term }));
      const hasExactMatch = matchingExisting.some(
        (item) => item.id === trimmed,
      );
      if (hasExactMatch || !isValidTextTerm(trimmed, inputType)) {
        return matchingExisting;
      }
      return [
        { id: trimmed, label: trimmed, isCreatable: true },
        ...matchingExisting,
      ];
    },
    resolveSelectedItems: async (ids) => ids.map((id) => ({ id, label: id })),
    itemLabels: { singular: 'term', plural: 'terms' },
    showItemAvatar: false,
    showStartTypingHint,
  };
}

export function useTextFilterOptions(
  value: MultiSelectFilterValue | undefined,
  inputType: MultiSelectFilterTextInputType = 'string',
): MultiSelectFilterOptions {
  return useMemo(
    () =>
      buildTextTermOptions(
        value,
        inputType,
        inputType === 'string' || inputType === 'text',
      ),
    [value, inputType],
  );
}

type Dispatch = ReturnType<typeof useAppDispatch>;

// Plain (non-hook) factories so a single resolver can build options for any
// dynamic metadata field without violating the rules of hooks.

function makeTagCloudOptions(
  dispatch: Dispatch,
  fieldName: string,
): MultiSelectFilterOptions {
  return {
    loadOptions: async (query) => {
      const trimmed = query.trim();
      if (!trimmed) {
        return [];
      }
      const response = await dispatch(
        getSearchSuggestions({ query: trimmed, field_name: fieldName }),
      );
      // A suggestion with no value has nothing to show or select.
      return response.body.objects.flatMap((suggestion) =>
        suggestion.value
          ? [{ id: suggestion.value, label: suggestion.value }]
          : [],
      );
    },
    resolveSelectedItems: async (ids) => ids.map((id) => ({ id, label: id })),
    itemLabels: { singular: 'tag', plural: 'tags' },
    showItemAvatar: false,
    showStartTypingHint: true,
    startTypingHint: 'Start typing to search',
  };
}

export function makeDropDownOptions(
  options: MetadataFieldOptionsSchema[],
): MultiSelectFilterOptions {
  return {
    loadOptions: async (query) => {
      const trimmed = query.trim().toLowerCase();
      return options
        .filter(
          (option): option is MetadataFieldOptionsSchema & { value: string } =>
            option.value !== undefined &&
            (!trimmed ||
              (option.label?.toLowerCase().includes(trimmed) ?? false) ||
              option.value.toLowerCase().includes(trimmed)),
        )
        .map((option) => ({
          id: option.value,
          label: option.label ?? option.value,
        }));
    },
    resolveSelectedItems: async (ids) =>
      ids.map((id) => {
        const option = options.find(({ value }) => value === id);
        return { id, label: option?.label ?? id };
      }),
    itemLabels: { singular: 'value', plural: 'values' },
    showItemAvatar: false,
    showStartTypingHint: false,
  };
}

function makeTextMetadataOptions(
  value: MultiSelectFilterValue | undefined,
  inputType: MultiSelectFilterTextInputType,
): MultiSelectFilterOptions {
  return buildTextTermOptions(value, inputType, inputType === 'string');
}

export type MetadataFilterFieldDefinition = {
  name?: string | null;
  field_type?: string | null;
  options?: MetadataFieldOptionsSchema[] | null;
};

/**
 * Returns a resolver that builds the multi-select value editor options for any
 * dynamic metadata field (tag_cloud, drop_down, or a text type) from its
 * definition. Returns undefined for field types without an inline value editor.
 */
export function useMetadataFilterOptions(): (
  field: MetadataFilterFieldDefinition | undefined,
  value?: MultiSelectFilterValue,
) => MultiSelectFilterOptions | undefined {
  const dispatch = useAppDispatch();

  return useCallback((field, value) => {
    if (!field?.name) {
      return undefined;
    }
    if (field.field_type === 'tag_cloud') {
      return makeTagCloudOptions(dispatch, field.name);
    }
    if (field.field_type === 'drop_down') {
      return makeDropDownOptions(field.options ?? []);
    }
    if (isTextInputType(field.field_type)) {
      return makeTextMetadataOptions(value, field.field_type);
    }
    return undefined;
  }, []);
}
